import { useState } from "react";
import { clsx } from "clsx";
import { Download, FilePlus2, Paperclip, Undo2, UserRound, Wallet, Workflow } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { EmptyState, SectionCard } from "../ui";
import { fmtDateTime } from "../../utils";
import type { OrderEventKind, OrderHistoryEvent } from "../../types";

const ICONS: Record<OrderEventKind, typeof Workflow> = {
  created: FilePlus2,
  status: Workflow,
  revert: Undo2,
  fee: Wallet,
  poa_fee: Wallet,
  file: Paperclip,
  orderer: UserRound,
};

// Cofnięcia mają być widoczne na pierwszy rzut oka — to one tłumaczą, dlaczego
// zamówienie stoi „wcześniej", niż wynikałoby z dat wysyłki/odbioru.
const TONES: Record<OrderEventKind, string> = {
  created: "text-[var(--color-text-muted)]",
  status: "text-[var(--color-primary)]",
  revert: "text-amber-600 dark:text-amber-400",
  fee: "text-[var(--color-text-secondary)]",
  poa_fee: "text-[var(--color-text-secondary)]",
  file: "text-[var(--color-text-secondary)]",
  orderer: "text-[var(--color-primary)]",
};

const PAGE = 12;

/** Historia JEDNEGO zamówienia urzędowego (MZ / wypisy / MPZP) — od najnowszego wpisu.
 *  Kształt wpisu jest wspólny dla trzech modułów (backend: order_history.OrderHistory). */
export function OrderHistorySection({ events }: { events: OrderHistoryEvent[] }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? events : events.slice(0, PAGE);

  return (
    <SectionCard title={`Historia (${events.length})`}>
      {events.length === 0 ? (
        <EmptyState>
          Brak wpisów — historia zapisuje zmiany statusu (także cofnięcia), opłaty
          i operacje na plikach ze starostwa.
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {shown.map((e) => {
            const Icon = ICONS[e.kind] ?? Workflow;
            return (
              <div key={e.id} className="flex gap-2 text-xs">
                <Icon size={14} className={clsx("mt-0.5 shrink-0", TONES[e.kind])} />
                <div className="min-w-0">
                  <div className="whitespace-pre-wrap">{e.description}</div>
                  {/* Plik usunięty z wniosku żyje dalej TYLKO przy swoim wpisie —
                      stąd link do pobrania także dla plików, których już nie ma
                      w żadnej sekcji zamówienia. */}
                  {e.file?.url && (
                    <a
                      href={e.file.url}
                      download={e.file.original_filename}
                      title={`Pobierz: ${e.file.original_filename}`}
                      className="inline-flex items-center gap-1 mt-0.5 font-mono border border-[var(--color-border-strong)] px-2 py-0.5 hover:bg-[var(--color-bg-elevated)]"
                    >
                      <Download size={11} /> {e.file.original_filename}
                    </a>
                  )}
                  <div className="text-[11px] text-[var(--color-text-muted)]">
                    <span className="font-mono uppercase tracking-wide">{e.kind_display}</span>
                    {" · "}
                    {e.actor_name || "automatycznie (/official-fees)"}
                    {" · "}
                    {fmtDateTime(e.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
          {events.length > PAGE && (
            <Button size="sm" variant="ghost" onClick={() => setExpanded((v) => !v)}>
              {expanded ? "Pokaż mniej" : `Pokaż wszystkie (${events.length})`}
            </Button>
          )}
        </div>
      )}
    </SectionCard>
  );
}
